import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import AuthModal from './AuthModal/AuthModal'
import ProfilePillMenu from './ProfilePillMenu'
import LanguageSelector from './LanguageSelector'
import { apiLogout } from '../api/auth/authApi'

type NavLink = {
  label: string
  to: string
}

const navLinks: NavLink[] = [
  { label: 'Home', to: '/' },
  { label: 'Day Trips', to: '/day-trips' },
  { label: 'Mini Van Tours', to: '/private-tours' },
  { label: 'Ski Transfers', to: '/ski-transfers' },
  { label: 'Fleets', to: '/fleets' },
  { label: 'About us', to: '/about' },
]

const mobileAccountLinks = [
  {
    label: 'Saved Places',
    to: '/saved-places',
    icon: '/assets/images/pill-wishlist.svg',
  },
  {
    label: 'My Trips',
    to: '/my-trips',
    icon: '/assets/images/pill-trips.svg',
  },
  {
    label: 'Notifications',
    to: '/notifications',
    icon: '/assets/images/pill-notifications.svg',
  },
  {
    label: 'Profile',
    to: '/profile',
    icon: '/assets/images/pill-profile.svg',
  },
] as const

const PROFILE_STORAGE_KEY = 'salzburg_profile_details'

/** Name shown in the account pill from the authenticated user data. */
const getGreetingName = (_email: string | null) => {
  try {
      const storedProfile = localStorage.getItem(PROFILE_STORAGE_KEY)
    if (storedProfile) {
      const profile = JSON.parse(storedProfile) as {
        name?: string
        full_name?: string
        first_name?: string
        last_name?: string
      }
      const profileName = profile.name?.trim() || profile.full_name?.trim() ||
        [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim()
      if (profileName) return profileName
    }
    // Prefer the canonical user_data object (if present)
    const rawUser = localStorage.getItem('user_data')
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as Record<string, any>
      const firstName = parsed.first_name?.trim()
      const lastName = parsed.last_name?.trim()
      const nameField = parsed.name?.trim() || parsed.full_name?.trim()
      const username = parsed.username?.trim()

      if (firstName && lastName) {
        return `${firstName} ${lastName.charAt(0).toUpperCase()}`
      }

      const full = `${firstName || ''} ${lastName || ''}`.trim()
      if (full) {
        const tokens = full.split(/\s+/)
        if (tokens.length > 1) return `${tokens[0]} ${tokens[1].charAt(0).toUpperCase()}`
        return tokens[0]
      }

      if (nameField) {
        const tokens = nameField.split(/\s+/)
        if (tokens.length > 1) return `${tokens[0]} ${tokens[1].charAt(0).toUpperCase()}`
        return tokens[0]
      }

      if (username) {
        const tokens = username.split(/\s+/)
        return tokens[0]
      }
    }
  } catch {
    // fall through
  }

 return 'there'
}

type HeaderProps = {
  absolute?: boolean

  /** Tighter vertical padding for nested page shells. */
  compact?: boolean

  /** Hide the pill dropdown on account pages. */
  hideProfileMenu?: boolean
}

export default function Header({
  absolute = false,
  compact = false,
  hideProfileMenu = false,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] =
    useState(false)

  const [authOpen, setAuthOpen] =
    useState(false)

  const [
    profileMenuOpen,
    setProfileMenuOpen,
  ] = useState(false)

  const [userEmail, setUserEmail] =
    useState<string | null>(null)

  const [greetingName, setGreetingName] =
    useState('there')

  const [scrolled, setScrolled] =
    useState(false)

  const profileMenuRef =
    useRef<HTMLDivElement>(null)

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const saved =
      localStorage.getItem('user_email')

    if (saved) {
      setUserEmail(saved)

      setGreetingName(
        getGreetingName(saved),
      )
    }
  }, [])

  useEffect(() => {
    const onProfileUpdated = () => {
      setGreetingName(getGreetingName(userEmail))
    }

    window.addEventListener('customer-profile-updated', onProfileUpdated)
    return () => {
      window.removeEventListener('customer-profile-updated', onProfileUpdated)
    }
  }, [userEmail])

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12)
    }

    onScroll()

    window.addEventListener(
      'scroll',
      onScroll,
      {
        passive: true,
      },
    )

    return () => {
      window.removeEventListener(
        'scroll',
        onScroll,
      )
    }
  }, [])

  useEffect(() => {
    setProfileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!profileMenuOpen) {
      return
    }

    const onPointerDown = (
      event: MouseEvent,
    ) => {
      if (
        !profileMenuRef.current?.contains(
          event.target as Node,
        )
      ) {
        setProfileMenuOpen(false)
      }
    }

    const onKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (event.key === 'Escape') {
        setProfileMenuOpen(false)
      }
    }

    document.addEventListener(
      'mousedown',
      onPointerDown,
    )

    document.addEventListener(
      'keydown',
      onKeyDown,
    )

    return () => {
      document.removeEventListener(
        'mousedown',
        onPointerDown,
      )

      document.removeEventListener(
        'keydown',
        onKeyDown,
      )
    }
  }, [profileMenuOpen])

  const handleLoginSuccess = (
    email: string,
  ) => {
    setUserEmail(email)

    setGreetingName(
      getGreetingName(email),
    )
  }

  const handleLogout = async () => {
    await apiLogout()

    localStorage.removeItem('user_email')
    localStorage.removeItem('auth_token')

    setUserEmail(null)
    setGreetingName('there')
    setProfileMenuOpen(false)

    navigate('/')
  }

  const showSolid =
    scrolled || !absolute

  return (
    <>
      <header
        className={[
          'fixed inset-x-0 top-0 z-[100] transition-[background-color,box-shadow,padding] duration-200',

          showSolid
            ? 'bg-white shadow-[0_1px_12px_rgba(0,0,0,0.06)]'
            : 'bg-transparent',

          absolute && !scrolled
            ? 'px-[clamp(24px,4.8vw,72px)] pt-[clamp(24px,3.8vh,48px)] pb-[clamp(8px,1.2vh,12px)]'
            : compact
              ? 'px-[clamp(24px,4.8vw,72px)] py-[clamp(10px,1.6vh,16px)]'
              : 'px-[clamp(24px,4.8vw,72px)] py-[clamp(12px,2vh,24px)]',
        ].join(' ')}
      >
        <div className="flex w-full items-center justify-between gap-6">


          <Link
            to="/"
            className="inline-block shrink-0"
          >
            <img
              src="/assets/images/logo.png"
              alt="Salzburg Cab"
              className="h-auto w-[clamp(66px,7.4vw,102px)] object-contain"
            />
          </Link>

      
          <button
            type="button"
            className="ml-auto flex flex-col gap-1.5 md:hidden"
            aria-label="Toggle menu"
            onClick={() =>
              setMenuOpen(!menuOpen)
            }
          >
            <span className="block h-0.5 w-6 bg-text-primary" />
            <span className="block h-0.5 w-6 bg-text-primary" />
            <span className="block h-0.5 w-6 bg-text-primary" />
          </button>

          {/* =========================
              DESKTOP NAV
          ========================== */}
          <nav className="hidden md:flex md:flex-1 md:flex-row md:items-center md:justify-center md:gap-[clamp(20px,2.8vw,40px)]">
            {navLinks.map((link) => {
              const isActive =
                link.to === '/'
                  ? location.pathname === '/' &&
                    link.label === 'Home'
                  : location.pathname.startsWith(
                      link.to,
                    )

              return (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`whitespace-nowrap font-poppins text-[clamp(16px,0.92vw,16px)] leading-none tracking-[-0.022em] text-text-primary transition-colors hover:text-brand-orange ${
                    isActive
                      ? 'relative font-medium after:absolute after:-bottom-[9px] after:left-0 after:h-[1.5px] after:w-full after:bg-brand-orange'
                      : 'font-normal'
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
          </nav>

          {/* =========================
              DESKTOP RIGHT
          ========================== */}
          <div className="hidden shrink-0 items-center justify-end gap-[12px] md:flex">

            {/* Language */}
            <LanguageSelector />

            {/* Desktop logged-in user */}
            {userEmail ? (
              <div
                ref={profileMenuRef}
                className="relative"
              >
                <button
                  type="button"
                  aria-haspopup={
                    hideProfileMenu
                      ? undefined
                      : 'menu'
                  }
                  aria-expanded={
                    hideProfileMenu
                      ? undefined
                      : profileMenuOpen
                  }
                  aria-label={
                    hideProfileMenu
                      ? 'Account'
                      : 'Open account menu'
                  }
                  onClick={() => {
                    if (hideProfileMenu) {
                      return
                    }

                    setProfileMenuOpen(
                      (open) => !open,
                    )
                  }}
                  className="flex h-[clamp(36px,3.35vw,42px)] min-w-[138px] items-center justify-center gap-[4px] rounded-[22px] border border-solid border-[#B2ADAD] bg-white px-[22px] py-[9px] transition-transform active:scale-[0.98]"
                >
                  <img
                    src="/assets/images/header-user.png"
                    alt=""
                    aria-hidden
                    className="block size-[17px] max-w-none object-cover"
                  />

                  <span className="whitespace-nowrap font-poppins text-[14px] font-semibold leading-[1.75] tracking-[-0.42px] text-[#365E92]">
                    {`Hey ${greetingName}`}
                  </span>
                </button>

                {!hideProfileMenu && (
                  <ProfilePillMenu
                    variant="dropdown"
                    open={profileMenuOpen}
                    onClose={() =>
                      setProfileMenuOpen(
                        false,
                      )
                    }
                    onLogout={
                      handleLogout
                    }
                  />
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  setAuthOpen(true)
                }
                className="flex h-[clamp(27px,3.35vw,42px)] items-center gap-[clamp(3px,0.45vw,6px)] rounded-full border border-[#3570E98E] bg-white px-[clamp(10px,1.45vw,22px)] transition-transform active:scale-[0.98]"
              >
                <img
                  src="/assets/images/login-car.svg"
                  alt=""
                  aria-hidden
                />

                <span className="whitespace-nowrap font-poppins text-[clamp(8px,0.92vw,14px)] font-semibold leading-none text-brand-blue">
                  Sign In
                </span>
              </button>
            )}
          </div>
        </div>

        {menuOpen && (
          <div
            className="
              fixed
              inset-0
              z-[300]
              flex
              h-[100dvh]
              max-h-[100dvh]
              w-full
              flex-col
              overflow-hidden
              bg-[#FBFBFB]
              px-[20px]
              pb-[18px]
              pt-[24px]

              md:hidden
            "
          >

            <div className="flex shrink-0 items-start">
              <button
                type="button"
                aria-label="Close menu"
                onClick={() =>
                  setMenuOpen(false)
                }
                className="mt-[7px] flex h-[30px] w-[28px] shrink-0 items-center justify-start text-[#666A70]"
              >
                <svg
                  width="13"
                  height="23"
                  viewBox="0 0 14 25"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12.5 1.5L2 12.5L12.5 23.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <Link
                to="/"
                onClick={() =>
                  setMenuOpen(false)
                }
                className="ml-[20px]"
              >
                <img
                  src="/assets/images/logo.png"
                  alt="Salzburg Cab"
                  className="block h-auto w-[115px] object-contain"
                />
              </Link>
            </div>

           
            <div className="mt-[30px] flex shrink-0 items-center justify-between px-[3px]">

              {/* Logged in - display only */}
              {userEmail ? (
                <div className="flex items-center gap-[13px]">
                  <div className="flex h-[45px] w-[45px] shrink-0 items-center justify-center rounded-full bg-[#E8F0FF]">
                    <img
                      src="/assets/images/header-user.png"
                      alt=""
                      aria-hidden
                      className="h-[28px] w-[28px] object-contain"
                    />
                  </div>

                  <span className="whitespace-nowrap font-poppins text-[18px] font-medium leading-none tracking-[-0.025em] text-[#343434]">
                    Hey {greetingName}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    setAuthOpen(true)
                  }}
                  className="flex items-center gap-[13px]"
                >
                  <div className="flex h-[45px] w-[45px] shrink-0 items-center justify-center rounded-full bg-[#E8F0FF]">
                    <img
                      src="/assets/images/login-car.svg"
                      alt=""
                      aria-hidden
                      className="h-[26px] w-[26px] object-contain"
                    />
                  </div>

                  <span className="whitespace-nowrap font-poppins text-[18px] font-medium leading-none text-[#343434]">
                    Sign In
                  </span>
                </button>
              )}

              {/* Language */}
              <LanguageSelector variant="mobile" />
            </div>

            {/* Divider */}
            <div className="mt-[20px] h-px w-full shrink-0 bg-[#E4E4E4]" />

           
            <nav
              className="
                min-h-0
                flex-1
                overflow-y-auto
                pt-[4px]
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
              "
            >
          
              <div className="flex flex-col">
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() =>
                      setMenuOpen(false)
                    }
                    className="
                      flex
                      h-[58px]
                      shrink-0
                      items-center
                      border-b
                      border-[#E8E8E8]
                      px-[1px]
                      font-poppins
                      text-[17px]
                      font-normal
                      leading-none
                      tracking-[-0.02em]
                      text-[#3E4650]
                    "
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

           
              {userEmail && (
                <div className="flex flex-col">
                  {mobileAccountLinks.map(
                    (item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={() =>
                          setMenuOpen(false)
                        }
                        className="
                          flex
                          h-[58px]
                          shrink-0
                          items-center
                          gap-[20px]
                          border-b
                          border-[#E8E8E8]
                          px-[7px]
                          font-poppins
                          text-[17px]
                          font-normal
                          leading-none
                          tracking-[-0.02em]
                          text-[#4C525A]
                        "
                      >
                        <img
                          src={item.icon}
                          alt=""
                          aria-hidden
                          className="
                            h-[23px]
                            w-[23px]
                            shrink-0
                            object-contain
                            opacity-80
                          "
                        />

                        <span>
                          {item.label}
                        </span>
                      </Link>
                    ),
                  )}
                </div>
              )}
            </nav>

          
            <div className="shrink-0 bg-[#FBFBFB] pt-[14px]">

              {userEmail ? (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    handleLogout()
                  }}
                  className="
                    flex
                    h-[54px]
                    w-full
                    items-center
                    justify-center
                    gap-[12px]
                    rounded-[12px]
                    border-[1.5px]
                    border-[#E72E32]
                    bg-white
                    text-[#E72E32]
                    transition-transform
                    active:scale-[0.99]
                  "
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M10 4H6.5C5.67 4 5 4.67 5 5.5V18.5C5 19.33 5.67 20 6.5 20H10"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M14 8L18 12L14 16"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    <path
                      d="M9 12H18"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>

                  <span className="font-poppins text-[18px] font-normal leading-none">
                    Logout
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    setAuthOpen(true)
                  }}
                  className="
                    flex
                    h-[54px]
                    w-full
                    items-center
                    justify-center
                    rounded-[12px]
                    border-[1.5px]
                    border-[#3570E9]
                    bg-white
                    font-poppins
                    text-[18px]
                    font-medium
                    text-[#3570E9]
                    transition-transform
                    active:scale-[0.99]
                  "
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        )}

      
        <AuthModal
          isOpen={authOpen}
          onClose={() =>
            setAuthOpen(false)
          }
          onLoginSuccess={
            handleLoginSuccess
          }
        />
      </header>


      {!absolute && (
        <div
          className={
            compact
              ? 'h-[68px] shrink-0'
              : 'h-[88px] shrink-0'
          }
          aria-hidden
        />
      )}
    </>
  )
}